/**
 * Convert various deadline formats to a Date object
 * Handles: ISO 8601 strings, Firestore Timestamps, Date objects
 * @param {string|Date|object} deadline - The deadline in various formats
 * @returns {Date|null} - Date object or null if invalid
 */
export const convertDeadlineToDate = (deadline) => {
    if (!deadline) return null;

    try {
        // Handle Firestore Timestamp object with toDate method
        if (deadline.toDate && typeof deadline.toDate === "function") {
            return deadline.toDate();
        }

        // Handle Firestore Timestamp plain object with seconds property
        if (deadline.seconds && typeof deadline.seconds === "number") {
            return new Date(deadline.seconds * 1000);
        }

        // Handle regular Date object
        if (deadline instanceof Date) {
            return !isNaN(deadline.getTime()) ? deadline : null;
        }

        // Handle ISO 8601 string (e.g., "2026-03-08" or "2026-03-08T23:59:59Z")
        if (typeof deadline === "string") {
            const date = new Date(deadline);
            return !isNaN(date.getTime()) ? date : null;
        }

        // Fallback: try to create Date from whatever we have
        const date = new Date(deadline);
        return !isNaN(date.getTime()) ? date : null;
    } catch (error) {
        console.error("Error converting deadline to Date:", error);
        return null;
    }
};

/**
 * Get deadline timestamp in milliseconds
 * @param {string|Date|object} deadline - The deadline in various formats
 * @returns {number|null} - Timestamp in milliseconds or null if invalid
 */
export const getDeadlineTimestamp = (deadline) => {
    const date = convertDeadlineToDate(deadline);
    return date ? date.getTime() : null;
};

/**
 * Format deadline for display
 * @param {string|Date|object} deadline - The deadline in various formats
 * @param {object} options - Formatting options
 * @returns {string} - Formatted deadline string
 */
export const formatDeadlineDisplay = (deadline, options = {}) => {
    const date = convertDeadlineToDate(deadline);
    if (!date) return "Date TBD";

    const defaultOptions = {
        year: "numeric",
        month: "long",
        day: "numeric",
        ...options,
    };

    return date.toLocaleDateString("en-US", defaultOptions);
};

/**
 * Check if deadline has passed
 * @param {string|Date|object} deadline - The deadline in various formats
 * @returns {boolean} - True if deadline has passed
 */
export const hasDeadlinePassed = (deadline) => {
    const timestamp = getDeadlineTimestamp(deadline);
    return timestamp ? Date.now() > timestamp : false;
};

/**
 * Calculate countdown to deadline
 * @param {string|Date|object} deadline - The deadline in various formats
 * @returns {object} - Object with days, hours, minutes, seconds properties
 */
export const calculateCountdown = (deadline) => {
    const timestamp = getDeadlineTimestamp(deadline);

    if (!timestamp) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const now = Date.now();
    const distance = timestamp - now;

    if (distance <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
    };
};
